/**
 * JSON-LD Structured Data Generators for SEO
 *
 * These functions generate Schema.org structured data in JSON-LD format
 * for improved search engine visibility and rich results.
 */

import type { Bar, Special, Event } from '@/api/endpoints/bars';
import type { Location } from '@/api/endpoints/locations';

function getSiteUrl(): string {
  return import.meta.env.VITE_SITE_URL || 'http://localhost:5173';
}

/**
 * Generate LocalBusiness (BarOrPub) schema for a bar
 * @see https://schema.org/BarOrPub
 */
export function generateBarSchema(bar: Bar & { specials?: Special[]; events?: Event[] }) {
  const siteUrl = getSiteUrl();
  const barUrl = `${siteUrl}/bars/${bar.slug || bar.id}`;

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'BarOrPub',
    '@id': barUrl,
    name: bar.name,
    url: barUrl,
    address: {
      '@type': 'PostalAddress',
      streetAddress: bar.address,
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: bar.latitude,
      longitude: bar.longitude,
    },
  };

  if (bar.description) {
    schema.description = bar.description;
  }

  if (bar.logoUrl) {
    schema.image = bar.logoUrl;
  }

  if (bar.websiteUrl) {
    schema.sameAs = bar.websiteUrl;
  }

  if (bar.phoneNumber) {
    schema.telephone = bar.phoneNumber;
  }

  // Add opening hours if available
  if (bar.hours) {
    const openingHours: string[] = [];
    const dayMap: Record<string, string> = {
      monday: 'Mo',
      tuesday: 'Tu',
      wednesday: 'We',
      thursday: 'Th',
      friday: 'Fr',
      saturday: 'Sa',
      sunday: 'Su',
    };

    for (const [day, hours] of Object.entries(bar.hours)) {
      const abbrev = dayMap[day.toLowerCase()];
      if (abbrev && hours.open && hours.close) {
        openingHours.push(`${abbrev} ${hours.open}-${hours.close}`);
      }
    }

    if (openingHours.length > 0) {
      schema.openingHours = openingHours;
    }
  }

  // Add amenity features
  const amenityFeatures: string[] = [];
  if (bar.servesBeer) amenityFeatures.push('Serves Beer');
  if (bar.servesWine) amenityFeatures.push('Serves Wine');
  if (bar.servesCocktails) amenityFeatures.push('Serves Cocktails');
  if (bar.servesBrunch) amenityFeatures.push('Serves Brunch');
  if (bar.outdoorSeating) amenityFeatures.push('Outdoor Seating');
  if (bar.allowsDogs) amenityFeatures.push('Dog Friendly');
  if (bar.goodForWatchingSports) amenityFeatures.push('Sports Viewing');

  if (amenityFeatures.length > 0) {
    schema.amenityFeature = amenityFeatures.map(feature => ({
      '@type': 'LocationFeatureSpecification',
      name: feature,
      value: true,
    }));
  }

  // Add offers (specials) if available
  if (bar.specials && bar.specials.length > 0) {
    schema.makesOffer = bar.specials.slice(0, 10).map(special =>
      generateSpecialOffer(special, bar.name)
    );
  }

  // Add events if available
  if (bar.events && bar.events.length > 0) {
    schema.event = bar.events.slice(0, 10).map(event =>
      generateEventSchema(event, bar)
    );
  }

  return schema;
}

/**
 * Generate Offer schema for a special
 * @see https://schema.org/Offer
 */
export function generateSpecialOffer(special: Special, barName: string) {
  const offer: Record<string, unknown> = {
    '@type': 'Offer',
    name: special.name,
    offeredBy: {
      '@type': 'BarOrPub',
      name: barName,
    },
  };

  if (special.description) {
    offer.description = special.description;
  }

  if (special.price) {
    // Try to extract numeric price
    const priceMatch = special.price.match(/\$?(\d+(?:\.\d{2})?)/);
    if (priceMatch) {
      offer.price = parseFloat(priceMatch[1]);
      offer.priceCurrency = 'USD';
    } else {
      offer.priceSpecification = {
        '@type': 'PriceSpecification',
        price: special.price,
      };
    }
  }

  // Add category based on type
  if (special.type === 'drink') {
    offer.category = 'Drink Special';
  } else if (special.type === 'food') {
    offer.category = 'Food Special';
  } else if (special.type === 'combo') {
    offer.category = 'Combo Special';
  }

  if (special.isHappyHour) {
    offer.category = 'Happy Hour';
  }

  return offer;
}

/**
 * Generate Event schema
 * @see https://schema.org/Event
 */
export function generateEventSchema(event: Event, bar: Bar) {
  const siteUrl = getSiteUrl();

  const schema: Record<string, unknown> = {
    '@type': 'Event',
    name: event.name,
    location: {
      '@type': 'Place',
      name: bar.name,
      address: {
        '@type': 'PostalAddress',
        streetAddress: bar.address,
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: bar.latitude,
        longitude: bar.longitude,
      },
    },
    organizer: {
      '@type': 'BarOrPub',
      name: bar.name,
      url: `${siteUrl}/bars/${bar.slug || bar.id}`,
    },
  };

  if (event.description) {
    schema.description = event.description;
  }

  if (event.startDatetime) {
    schema.startDate = event.startDatetime;
  }

  if (event.endDatetime) {
    schema.endDate = event.endDatetime;
  }

  // Default event status
  schema.eventStatus = 'https://schema.org/EventScheduled';
  schema.eventAttendanceMode = 'https://schema.org/OfflineEventAttendanceMode';

  return schema;
}

/**
 * Generate BreadcrumbList schema for navigation
 * @see https://schema.org/BreadcrumbList
 */
export function generateBreadcrumbSchema(items: Array<{ name: string; path: string }>) {
  const siteUrl = getSiteUrl();

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${siteUrl}${item.path}`,
    })),
  };
}

/**
 * Generate ItemList schema for a list of bars (used on city/location pages)
 * @see https://schema.org/ItemList
 */
export function generateBarsListSchema(bars: Bar[], listName: string) {
  const siteUrl = getSiteUrl();

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: listName,
    numberOfItems: bars.length,
    itemListElement: bars.slice(0, 20).map((bar, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'BarOrPub',
        name: bar.name,
        url: `${siteUrl}/bars/${bar.slug || bar.id}`,
        address: {
          '@type': 'PostalAddress',
          streetAddress: bar.address,
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: bar.latitude,
          longitude: bar.longitude,
        },
      },
    })),
  };
}

/**
 * Generate Place schema for a location (city/neighborhood)
 * @see https://schema.org/Place
 */
export function generateLocationSchema(location: Location) {
  const siteUrl = getSiteUrl();

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Place',
    name: location.name + (location.state ? `, ${location.state}` : ''),
    url: `${siteUrl}/city/${location.slug}`,
    geo: {
      '@type': 'GeoCoordinates',
      latitude: location.latitude,
      longitude: location.longitude,
    },
  };

  if (location.description) {
    schema.description = location.description;
  }

  if (location.state) {
    schema.address = {
      '@type': 'PostalAddress',
      addressLocality: location.name,
      addressRegion: location.state,
      addressCountry: 'US',
    };
  }

  return schema;
}

/**
 * Generate WebSite schema for the main site
 * @see https://schema.org/WebSite
 */
export function generateWebSiteSchema() {
  const siteUrl = getSiteUrl();

  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Bar Guide',
    url: siteUrl,
    description: 'Find the best bar specials, happy hours, and events near you.',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/bars?search={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

/**
 * Generate Organization schema for the site
 * @see https://schema.org/Organization
 */
export function generateOrganizationSchema() {
  const siteUrl = getSiteUrl();

  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Bar Guide',
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    description: 'Your local guide to bar specials, happy hours, and events.',
  };
}
